import * as React from "react"

import { cn } from "@/lib/utils"

const isFirefox = typeof window !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

const TIME_OPTIONS = [];
if (isFirefox) {
  for (let h = 0; h < 24; h++) {
    const ampm = h >= 12 ? "PM" : "AM";
    const displayHour = h % 12 === 0 ? 12 : h % 12;
    const hh = String(displayHour).padStart(2, '0');
    TIME_OPTIONS.push(`${hh}:00 ${ampm}`);
    TIME_OPTIONS.push(`${hh}:30 ${ampm}`);
  }
}

// Convert 24h string "14:30" to AM/PM string "02:30 PM"
const convertTo12Hour = (time24) => {
  if (!time24) return "";
  const parts = time24.split(":");
  if (parts.length < 2) return time24;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];
  if (isNaN(hours)) return time24;
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const strHours = String(hours).padStart(2, '0');
  return `${strHours}:${minutes} ${ampm}`;
};

// Convert AM/PM string "02:30 PM" to 24h string "14:30"
const convertTo24Hour = (time12) => {
  if (!time12) return "";
  const trimTime = time12.trim();
  const match = trimTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return time12;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const ampm = match[3].toUpperCase();
  if (ampm === "PM" && hours < 12) hours += 12;
  if (ampm === "AM" && hours === 12) hours = 0;
  return `${String(hours).padStart(2, '0')}:${minutes}`;
};

const Input = React.forwardRef(({ className, type, onClick, onChange, value, ...props }, ref) => {
  const uniqueId = React.useId();
  const isFirefoxTime = isFirefox && type === "time";
  const datalistId = `firefox-time-list-${uniqueId}`;

  const handleClick = (e) => {
    if (type === "date" || type === "time" || type === "datetime-local" || isFirefoxTime) {
      if (!props.disabled && !props.readOnly && typeof e.currentTarget.showPicker === 'function') {
        try {
          e.currentTarget.showPicker();
        } catch (err) {
          // ignore
        }
      }
    }
    if (onClick) onClick(e);
  };

  const handleChange = (e) => {
    if (isFirefoxTime) {
      const rawValue = e.target.value;
      const convertedValue = convertTo24Hour(rawValue);
      const modifiedEvent = {
        ...e,
        target: {
          ...e.target,
          value: convertedValue,
        },
      };
      if (onChange) onChange(modifiedEvent);
    } else {
      if (onChange) onChange(e);
    }
  };

  const resolvedType = isFirefoxTime ? "text" : type;
  const resolvedValue = isFirefoxTime
    ? (value !== undefined ? convertTo12Hour(value) : undefined)
    : value;

  return (
    <>
      <input
        type={resolvedType}
        value={resolvedValue}
        onChange={handleChange}
        data-firefox-time={isFirefoxTime ? "true" : undefined}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          (type === "date" || type === "time" || type === "datetime-local") && !props.disabled && !props.readOnly && "cursor-pointer",
          className
        )}
        onClick={handleClick}
        ref={ref}
        list={isFirefoxTime ? datalistId : props.list}
        {...props}
      />
      {isFirefoxTime && (
        <datalist id={datalistId}>
          {TIME_OPTIONS.map(opt => (
            <option key={opt} value={opt} />
          ))}
        </datalist>
      )}
    </>
  );
})
Input.displayName = "Input"

export { Input }
